'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, Home, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

interface SignupFormData {
  fullName: string;
  email: string;
  phone: string;
  role: 'owner' | 'tenant';
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
}

interface SignupFormProps {
  onSwitchToLogin: () => void;
}

export default function SignupForm({ onSwitchToLogin }: SignupFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { signUp, signInWithGoogle } = useAuth();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
  } = useForm<SignupFormData>({
    defaultValues: { role: 'tenant' },
  });

  const password = watch('password');
  const role = watch('role');

  const onSubmit = async (data: SignupFormData) => {
    setIsLoading(true);
    try {
      const result = await signUp(data.email, data.password, {
        fullName: data.fullName,
        phone: data.phone,
        role: data.role,
      });

      // Check if email confirmation is required (session will be null)
      if (result?.session) {
        // Auto-confirmed — redirect immediately
        toast.success(`Account created! Welcome to Tolettu.com, ${data.fullName.split(' ')[0]}.`);
        if (data.role === 'owner') {
          router.push('/owner-dashboard');
        } else {
          router.push('/');
        }
        router.refresh();
      } else {
        // Email confirmation required
        toast.success('Account created! Please check your email to confirm your account, then sign in.', { duration: 6000 });
        onSwitchToLogin();
      }
    } catch (err: any) {
      const msg = err?.message || 'Failed to create account. Please try again.';
      setError('email', { message: msg });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
    } catch (err: any) {
      toast.error(err?.message || 'Google sign-up failed. Please try again.');
      setGoogleLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Create your account</h2>
        <p className="text-muted-foreground text-sm mt-1">
          List your property or find a home — it&apos;s free to join
        </p>
      </div>

      {/* Role Selector */}
      <div>
        <label className="label-text">I want to</label>
        <div className="grid grid-cols-2 gap-3 mt-1">
          <label
            className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 cursor-pointer transition-all duration-150 ${
              role === 'owner' ?'border-primary bg-secondary/50' :'border-border hover:border-primary/40'
            }`}
          >
            <input
              type="radio"
              value="owner"
              className="sr-only"
              {...register('role')}
            />
            <Home size={22} className={role === 'owner' ? 'text-primary' : 'text-muted-foreground'} />
            <div className="text-center">
              <div className={`text-sm font-bold ${role === 'owner' ? 'text-primary' : 'text-foreground'}`}>
                List Property
              </div>
              <div className="text-xs text-muted-foreground">I&apos;m a property owner</div>
            </div>
          </label>
          <label
            className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 cursor-pointer transition-all duration-150 ${
              role === 'tenant' ?'border-primary bg-secondary/50' :'border-border hover:border-primary/40'
            }`}
          >
            <input
              type="radio"
              value="tenant"
              className="sr-only"
              {...register('role')}
            />
            <Search size={22} className={role === 'tenant' ? 'text-primary' : 'text-muted-foreground'} />
            <div className="text-center">
              <div className={`text-sm font-bold ${role === 'tenant' ? 'text-primary' : 'text-foreground'}`}>
                Find Home
              </div>
              <div className="text-xs text-muted-foreground">I&apos;m looking to rent</div>
            </div>
          </label>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {/* Full Name */}
        <div>
          <label htmlFor="signup-name" className="label-text">Full Name</label>
          <input
            id="signup-name"
            type="text"
            autoComplete="name"
            placeholder="Priya Sharma"
            className={`input-field ${errors.fullName ? 'border-red-400' : ''}`}
            suppressHydrationWarning
            {...register('fullName', {
              required: 'Full name is required',
              minLength: { value: 2, message: 'Name must be at least 2 characters' },
            })}
          />
          {errors.fullName && <p className="error-text" role="alert">{errors.fullName.message}</p>}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="signup-email" className="label-text">Email Address</label>
          <input
            id="signup-email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            className={`input-field ${errors.email ? 'border-red-400' : ''}`}
            suppressHydrationWarning
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Enter a valid email address',
              },
            })}
          />
          {errors.email && <p className="error-text" role="alert">{errors.email.message}</p>}
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="signup-phone" className="label-text">Mobile Number</label>
          <p className="helper-text">Used for owner contact and OTP verification</p>
          <div className="relative mt-1.5">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground">
              +91
            </span>
            <input
              id="signup-phone"
              type="tel"
              autoComplete="tel"
              placeholder="98765 43210"
              className={`input-field pl-12 ${errors.phone ? 'border-red-400' : ''}`}
              suppressHydrationWarning
              {...register('phone', {
                required: 'Mobile number is required',
                pattern: {
                  value: /^[6-9]\d{9}$/,
                  message: 'Enter a valid 10-digit Indian mobile number',
                },
              })}
            />
          </div>
          {errors.phone && <p className="error-text" role="alert">{errors.phone.message}</p>}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="signup-password" className="label-text">Password</label>
          <p className="helper-text">Minimum 8 characters with at least one number</p>
          <div className="relative mt-1.5">
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Create a strong password"
              className={`input-field pr-11 ${errors.password ? 'border-red-400' : ''}`}
              suppressHydrationWarning
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Password must be at least 8 characters' },
                pattern: {
                  value: /^(?=.*[0-9]).{8,}$/,
                  message: 'Password must contain at least one number',
                },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="error-text" role="alert">{errors.password.message}</p>}
        </div>

        {/* Confirm Password */}
        <div>
          <label htmlFor="signup-confirm" className="label-text">Confirm Password</label>
          <div className="relative mt-1.5">
            <input
              id="signup-confirm"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Re-enter your password"
              className={`input-field pr-11 ${errors.confirmPassword ? 'border-red-400' : ''}`}
              suppressHydrationWarning
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (val) => val === password || 'Passwords do not match',
              })}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
            >
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.confirmPassword && <p className="error-text" role="alert">{errors.confirmPassword.message}</p>}
        </div>

        {/* Terms */}
        <div>
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 accent-primary rounded mt-0.5 shrink-0"
              suppressHydrationWarning
              {...register('agreeToTerms', {
                required: 'You must agree to the terms to continue',
              })}
            />
            <span className="text-sm text-muted-foreground font-medium leading-relaxed">
              I agree to Tolettu.com&apos;s{' '}
              <button type="button" className="text-primary font-semibold hover:underline">
                Terms of Service
              </button>{' '}
              and{' '}
              <button type="button" className="text-primary font-semibold hover:underline">
                Privacy Policy
              </button>
            </span>
          </label>
          {errors.agreeToTerms && <p className="error-text" role="alert">{errors.agreeToTerms.message}</p>}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full justify-center h-11 text-base"
          suppressHydrationWarning
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Creating account...
            </>
          ) : (
            'Create Account'
          )}
        </button>
      </form>

      {/* Switch to Login */}
      <p className="text-sm text-center text-muted-foreground">
        Already have an account?{' '}
        <button
          onClick={onSwitchToLogin}
          className="text-primary font-bold hover:underline"
        >
          Sign in
        </button>
      </p>

      {/* Google Sign-Up */}
      <div className="relative flex items-center gap-3">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground font-medium shrink-0">or sign up with</span>
        <div className="flex-1 h-px bg-border" />
      </div>
      <button
        type="button"
        onClick={handleGoogleSignUp}
        disabled={googleLoading}
        suppressHydrationWarning
        className="w-full flex items-center justify-center gap-3 h-11 rounded-xl border-2 border-border bg-card hover:border-primary/40 hover:bg-secondary/30 transition-all duration-150 text-sm font-semibold text-foreground disabled:opacity-60"
      >
        {googleLoading ? (
          <Loader2 size={18} className="animate-spin text-primary" />
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
        )}
        {googleLoading ? 'Redirecting...' : 'Sign up with Google'}
      </button>
    </div>
  );
}